import { BusyContent } from './ui/Primitives';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getBackendHost } from '../utils/backendHost';
import { logApiError } from '../utils/logger';
import { getReceptionistConfiguration } from '../services/receptionistConfigApi';
import './CalendarIntegrations.css';
import { rememberOnboardingOAuth, returnToOnboarding } from '../utils/onboardingOAuth';


const CALENDARS = [
  {
    id: 'google',
    name: 'Google Calendar',
    subtitle: 'Appointment scheduling',
    icon: '/images/google_calendar_icon.png',
    description: 'Check availability and add appointments to your primary Google Calendar.',
  },
  {
    id: 'calendly',
    name: 'Calendly',
    subtitle: 'Event-type scheduling',
    icon: '/images/calendly_icon.png',
    description: 'Offer appointment times from your connected Calendly event types.',
  },
];

function calendarOAuthFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const oauthError = params.get('error');
  if ((!code && !oauthError) || params.get('oauth')) return null;
  const requestedService = params.get('service');
  const service = requestedService === 'calendly' ? 'calendly' : 'google';
  return { service, code, error: oauthError, errorDescription: params.get('error_description') };
}

function clearCalendarOAuthQuery() {
  const url = new URL(window.location.href);
  ['code', 'service', 'scope', 'authuser', 'prompt', 'state', 'error', 'error_description'].forEach(key => url.searchParams.delete(key));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function CalendarIntegrations({ organization, isAdmin, onOrganizationChange, onboarding = false }) {
  const [currentOrganization, setCurrentOrganization] = useState(organization || {});
  const [selectedProvider, setSelectedProvider] = useState(() => calendarOAuthFromLocation()?.service || null);
  const [switchProvider, setSwitchProvider] = useState(null);
  const switchDialog = useRef(null);
  useEffect(() => {
    if (!switchProvider) return;
    const previousFocus = document.activeElement;
    switchDialog.current.showModal();
    return () => previousFocus?.focus();
  }, [switchProvider]);
  const [action, setAction] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [providerChangePending, setProviderChangePending] = useState(false);
  const oauthHandled = useRef(false);
  const backendHost = getBackendHost();
  const jwtToken = localStorage.getItem('jwt_authorization');

  useEffect(() => setCurrentOrganization(organization || {}), [organization]);

  const refreshPublishStatus = useCallback(async () => {
    try {
      const configuration = await getReceptionistConfiguration();
      setProviderChangePending(Boolean(configuration?.changedFields?.some(field => field.key === 'calendarProvider')));
    } catch (_error) {
      // A publishing-status failure must not undo a successful OAuth connection.
    }
  }, []);

  useEffect(() => { refreshPublishStatus(); }, [refreshPublishStatus]);

  const refreshOrganization = useCallback(async () => {
    const response = await axios.post(`${backendHost}/organization`, { jwt_token: jwtToken });
    const nextOrganization = response.data.organization || {};
    setCurrentOrganization(nextOrganization);
    onOrganizationChange?.(nextOrganization);
    await refreshPublishStatus();
    return nextOrganization;
  }, [backendHost, jwtToken, refreshPublishStatus, onOrganizationChange]);

  useEffect(() => {
    const oauth = calendarOAuthFromLocation();
    if (!oauth || oauthHandled.current || !currentOrganization.id || !jwtToken) return;
    oauthHandled.current = true;
    setSelectedProvider(oauth.service);
    if (oauth.error) {
      setError(oauth.errorDescription || 'The calendar connection was not approved. You can try again when you are ready.');
      clearCalendarOAuthQuery();
      returnToOnboarding({ organizationId: currentOrganization.id, provider: oauth.service, kind: 'calendar', success: false });
      return;
    }
    setAction(`${oauth.service}-oauth`);
    setError('');
    axios.post(`${backendHost}/oauth`, {
      jwt_token: jwtToken,
      code: oauth.code,
      service: oauth.service,
    }).then(async () => {
      await refreshOrganization();
      setNotice(`${oauth.service === 'google' ? 'Google Calendar' : 'Calendly'} connected successfully.`);
      clearCalendarOAuthQuery();
      returnToOnboarding({ organizationId: currentOrganization.id, provider: oauth.service, kind: 'calendar', success: true });
    }).catch(requestError => {
      logApiError({ page: 'Integrations', operation: `${oauth.service}OAuth`, error: requestError });
      setError(`${oauth.service === 'google' ? 'Google Calendar' : 'Calendly'} could not complete the connection. Please try again.`);
      clearCalendarOAuthQuery();
      returnToOnboarding({ organizationId: currentOrganization.id, provider: oauth.service, kind: 'calendar', success: false });
    }).finally(() => setAction(''));
  }, [backendHost, jwtToken, refreshOrganization, currentOrganization.id]);

  const isConnected = provider => (
    provider === 'google'
      ? Boolean(currentOrganization.is_google_connected)
      : Boolean(currentOrganization.is_calendly_connected)
  );

  const otherProviderConnected = provider => (
    provider === 'google'
      ? isConnected('calendly')
      : isConnected('google')
  );

  const connect = async provider => {
    setAction(`${provider}-connect`);
    setNotice('');
    setError('');
    try {
      const response = await axios.post(`${backendHost}/oauth`, {
        jwt_token: jwtToken,
        service: provider,
      });
      rememberOnboardingOAuth({ onboarding, organizationId: currentOrganization.id, provider, kind: 'calendar' });
      window.location.assign(response.data.auth_url);
    } catch (requestError) {
      logApiError({ page: 'Integrations', operation: `${provider}Connect`, error: requestError });
      setError(`${provider === 'google' ? 'Google Calendar' : 'Calendly'} connection could not be started.`);
      setAction('');
    }
  };

  const disconnect = async provider => {
    setAction(`${provider}-disconnect`);
    setNotice('');
    setError('');
    try {
      if (provider === 'google') {
        await axios.put(`${backendHost}/user`, {
          jwt_token: jwtToken,
          google_refresh_token: null,
          service: 'google',
        });
      } else {
        await axios.put(`${backendHost}/v2/organization/${currentOrganization.id}`, {
          jwt_token: jwtToken,
          calendly_calendar_id: null,
          calendly_refresh_token: null,
        });
      }
      await refreshOrganization();
      setNotice(`${provider === 'google' ? 'Google Calendar' : 'Calendly'} disconnected.`);
    } catch (requestError) {
      logApiError({ page: 'Integrations', operation: `${provider}Disconnect`, error: requestError });
      setError(`${provider === 'google' ? 'Google Calendar' : 'Calendly'} could not be disconnected.`);
    } finally {
      setAction('');
    }
  };

  const selectedCalendar = CALENDARS.find(calendar => calendar.id === selectedProvider);
  const selectedConnected = selectedCalendar && isConnected(selectedCalendar.id);
  const selectedSwitch = selectedCalendar && !selectedConnected && otherProviderConnected(selectedCalendar.id);
  const selectedConnecting = action === `${selectedProvider}-connect` || action === `${selectedProvider}-oauth`;
  const selectedDisconnecting = action === `${selectedProvider}-disconnect`;
  const selectedStatus = selectedConnected ? 'Connected' : 'Not connected';
  const anyConnected = CALENDARS.some(calendar => isConnected(calendar.id));

  return (
    <section className="calendar-integrations" aria-labelledby="calendar-integrations-title">
      <div className="calendar-integrations-intro">
        <div><div><span className="say-eyebrow">Scheduling</span><h2 id="calendar-integrations-title">Choose a calendar</h2></div></div>
        <p>Calendar connections are managed here. Appointment hours and caller behavior stay with your Receptionist.</p>
      </div>
      {error ? <div className="calendar-integration-notice error" role="alert">{error}</div> : null}
      {notice ? <div className="calendar-integration-notice success" role="status">{notice}</div> : null}
      {!isAdmin ? <div className="calendar-integration-notice">Only organization admins can modify calendar integrations.</div> : null}
      <div className="calendar-integration-grid">
        {CALENDARS.map(calendar => {
          const connected = isConnected(calendar.id);
          return (
            <button type="button" className={`crm-provider-card calendar-provider-card ${selectedProvider === calendar.id ? 'selected' : ''}`}
              key={calendar.id} onClick={() => setSelectedProvider(calendar.id)}
              aria-expanded={selectedProvider === calendar.id} aria-controls="calendar-connection-details">
              <span className="crm-provider-mark calendar-provider-mark"><img src={calendar.icon} alt="" /></span>
              <span className="crm-provider-card-copy"><strong>{calendar.name}</strong><small>{calendar.subtitle}</small></span>
              <span className={`crm-card-status ${connected ? 'connected' : ''}`}>{connected ? 'Connected' : 'Not connected'}</span>
              <span className="crm-card-arrow" aria-hidden="true">›</span>
            </button>
          );
        })}
      </div>
      <div id="calendar-connection-details">
        {selectedCalendar ? <section className="crm-provider-workspace calendar-connection-workspace" aria-label={`${selectedCalendar.name} connection`} aria-busy={Boolean(action)}>
          <div className="crm-workspace-hero">
            <div className="crm-workspace-title">
              <span className="crm-provider-mark calendar-provider-mark"><img src={selectedCalendar.icon} alt="" /></span>
              <div><span>Calendar integration</span><h2>{selectedCalendar.name}</h2><p>{selectedCalendar.subtitle}</p></div>
            </div>
            <span className={`crm-status-pill ${selectedConnected ? 'connected' : selectedSwitch ? 'inactive' : ''}`}>{selectedStatus}</span>
          </div>
          <div className="crm-setup-grid">
            <div className="crm-setup-instructions">
              <h3>Connect {selectedCalendar.name}</h3>
              <ol>
                <li>Sign in to the {selectedCalendar.name} account you want Sayable to use.</li>
                <li>Review and approve the requested calendar permissions.</li>
                <li>Configure appointment booking on Receptionist, then review and publish your changes.</li>
              </ol>
            </div>
            <div className="crm-connection-panel">
              <h3>Calendar connection</h3>
              <p className="calendar-connection-description">{selectedCalendar.description}</p>
              {selectedSwitch ? <p className="crm-unavailable-copy">Sayable uses one calendar provider at a time. Connecting {selectedCalendar.name} successfully will disconnect {selectedCalendar.id === 'google' ? 'Calendly' : 'Google Calendar'}.</p> : null}
              <div className="crm-actions">
                {!selectedConnected ? <button type="button" className="crm-primary-button" onClick={() => selectedSwitch ? setSwitchProvider(selectedCalendar) : connect(selectedCalendar.id)} disabled={!isAdmin || Boolean(action)}>
                  <BusyContent busy={selectedConnecting} label={"Opening secure connection…"}>{`Connect ${selectedCalendar.name}`}</BusyContent>
                </button> : <button type="button" className="crm-secondary-button danger" onClick={() => disconnect(selectedCalendar.id)} disabled={!isAdmin || Boolean(action)}>
                  <BusyContent busy={selectedDisconnecting} label={"Disconnecting…"}>{'Disconnect'}</BusyContent>
                </button>}
              </div>
            </div>
          </div>
        </section> : null}
      </div>
      {switchProvider ? <dialog ref={switchDialog} className="calendar-switch-modal" aria-labelledby="calendar-switch-title" aria-describedby="calendar-switch-description" onCancel={() => setSwitchProvider(null)}>
        <h2 id="calendar-switch-title">Switch calendar provider?</h2>
        <p id="calendar-switch-description">Your current calendar will stay connected while you connect {switchProvider.name}. Once the new connection succeeds, {switchProvider.id === 'google' ? 'Calendly' : 'Google Calendar'} will be disconnected from Sayable. If you cancel or the connection fails, your current calendar will stay connected.</p>
        <p>Review and publish your calendar change on Receptionist after connecting.</p>
        <div className="crm-actions">
          <button type="button" className="crm-secondary-button" autoFocus onClick={() => setSwitchProvider(null)}>Cancel</button>
          <button type="button" className="crm-primary-button" onClick={() => { const provider = switchProvider.id; setSwitchProvider(null); connect(provider); }}>Continue to {switchProvider.name}</button>
        </div>
      </dialog> : null}
      {!onboarding && (anyConnected || providerChangePending) ? <div className="calendar-integration-handoff"><div><strong>{providerChangePending ? 'Calendar change ready to review' : 'Calendar connected'}</strong><span>{providerChangePending ? 'Review and publish this change on Receptionist.' : 'Choose appointment hours, event type, and caller behavior on Receptionist.'}</span></div><Link to="/receptionist#workflows">{providerChangePending ? 'Review and publish →' : 'Manage appointment booking →'}</Link></div> : null}
    </section>
  );
}

export default CalendarIntegrations;
